import { followAnswer } from './follow-answer';
import { settledGeneration } from './push';
import { sameRecord } from './same-record';
import type { SyncApi } from './sync-api';
import {
    answerRecordFrom,
    entityKeys,
    findingPhotoRecordFrom,
    findingRecordFrom,
    inspectionRecordFrom,
    multiCountRecordFrom,
    orderRecordFrom,
    photoRecordFrom,
} from './sync-records';
import { SYNC_META_KEYS, type AnswerRecord, type TemplateVersionRecord } from './records';
import type { TenantDatabase } from './tenant-db';
import type { BootstrapData, WireInspection, WireTemplateVersion } from './wire';

/**
 * `GET /sync/bootstrap` in de database van de omgeving zetten.
 *
 * De bootstrap is de volledige waarheid van de server voor dit apparaat, met
 * één uitzondering: **wat nog in de outbox staat, wint lokaal**. Een antwoord
 * dat de inspecteur offline gaf en dat nog niet verstuurd is, wordt dus niet
 * overschreven en ook niet weggegooid; de push beslist daarna op
 * `client_updated_at`. Hetzelfde geldt voor foto's die nog geüpload moeten
 * worden.
 *
 * Alles gebeurt in één transactie: de opname ziet de oude of de nieuwe
 * toestand, nooit een half bijgewerkte.
 */
export async function applyBootstrap(
    db: TenantDatabase,
    data: BootstrapData,
    now: number = Date.now(),
    settled?: number,
): Promise<boolean> {
    return db.transaction(
        'rw',
        [
            db.templateVersions,
            db.packageDocuments,
            db.orders,
            db.inspections,
            db.answers,
            db.multiCounts,
            db.findings,
            db.photos,
            db.outbox,
            db.syncMeta,
        ],
        async () => {
            /* Zie `SYNC_META_KEYS.settled`: ouder dan wat hier staat, dus opnieuw ophalen. */
            if (settled !== undefined && (await settledGeneration(db)) !== settled) {
                return false;
            }

            const pending = new Set((await db.outbox.toArray()).map((op) => op.entityKey));

            await announceTemplateVersions(db, data.template_versions);

            const orderUuids = new Set(data.orders.map((order) => order.uuid));
            const orders = data.orders.map(orderRecordFrom);
            const storedOrders = await db.orders.bulkGet(orders.map((order) => order.uuid));
            await db.orders.bulkPut(
                orders.filter((order, index) => !sameRecord(storedOrders[index], order)),
            );

            const inspectionUuids = new Set(data.inspections.map((inspection) => inspection.uuid));

            for (const inspection of data.inspections) {
                await mergeInspection(db, inspection, pending, now);
            }

            for (const local of await db.inspections.toArray()) {
                if (!inspectionUuids.has(local.uuid) && !(await hasLocalWork(db, local.uuid))) {
                    await removeInspection(db, local.uuid);
                }
            }

            for (const order of await db.orders.toArray()) {
                const keep =
                    orderUuids.has(order.uuid) ||
                    (order.inspectionUuid !== null &&
                        (await db.inspections.get(order.inspectionUuid)) !== undefined);

                if (!keep) {
                    await db.orders.delete(order.uuid);
                }
            }

            await pruneTemplateVersions(db, data.template_versions);

            await db.syncMeta.bulkPut([
                { key: SYNC_META_KEYS.cursor, value: data.cursor },
                { key: SYNC_META_KEYS.bootstrappedAt, value: now },
                { key: SYNC_META_KEYS.user, value: data.user },
            ]);

            return true;
        },
    );
}

/** Kondigt de benodigde pakketten aan. Een al opgehaald document blijft staan tot er een nieuw is. */
export async function announceTemplateVersions(
    db: TenantDatabase,
    versions: readonly WireTemplateVersion[],
): Promise<void> {
    for (const version of versions) {
        const existing = await db.templateVersions.get(version.uuid);
        const record: TemplateVersionRecord = {
            uuid: version.uuid,
            version: version.version,
            checksum: version.checksum,
            packageUrl: version.package_url,
            etag: existing?.etag ?? null,
            byteSize: existing?.byteSize ?? 0,
            fetchedAt: existing?.fetchedAt ?? null,
        };

        await db.templateVersions.put(record);
    }
}

async function pruneTemplateVersions(
    db: TenantDatabase,
    versions: readonly WireTemplateVersion[],
): Promise<void> {
    const needed = new Set(versions.map((version) => version.uuid));

    for (const inspection of await db.inspections.toArray()) {
        if (inspection.templateVersionUuid) {
            needed.add(inspection.templateVersionUuid);
        }
    }

    const obsolete = (await db.templateVersions.toCollection().primaryKeys()).filter(
        (uuid) => !needed.has(uuid),
    );

    await db.templateVersions.bulkDelete(obsolete);
    await db.packageDocuments.bulkDelete(obsolete);
}

/**
 * Eén opname uit bootstrap samenvoegen. `pending` bevat de outbox-sleutels
 * van wat lokaal nog verstuurd moet worden.
 */
export async function mergeInspection(
    db: TenantDatabase,
    inspection: WireInspection,
    pending: ReadonlySet<string>,
    now: number,
): Promise<void> {
    const uuid = inspection.uuid;
    const local = await db.inspections.get(uuid);
    /* Wegschrijven alleen wat verschilt: zie `sameRecord`. */
    const next = pending.has(entityKeys.inspection(uuid))
        ? local && {
              ...local,
              orderUuid: inspection.order_uuid,
              templateVersionUuid: inspection.template_version_uuid,
          }
        : inspectionRecordFrom(inspection, local);

    if (next && !sameRecord(local, next)) {
        await db.inspections.put(next);
    }

    /* Antwoorden, per (vraag, bereik). */
    const serverAnswers = inspection.answers.map((answer) => answerRecordFrom(uuid, answer));
    const serverUuidBySlot = new Map(
        serverAnswers.map((answer) => [answer.slot, answer.answerUuid]),
    );
    const localAnswers = await db.answers.where('inspectionUuid').equals(uuid).toArray();
    const localByUuid = new Map(localAnswers.map((answer) => [answer.answerUuid, answer]));
    const storedAnswers = new Map<string, AnswerRecord>();

    for (const local of localAnswers) {
        const keepLocal = pending.has(entityKeys.answer(uuid, local.slot));

        if (!keepLocal && serverUuidBySlot.get(local.slot) !== local.answerUuid) {
            await db.answers.delete(local.answerUuid);
        } else {
            storedAnswers.set(local.answerUuid, local);
        }
    }

    for (const answer of serverAnswers) {
        if (
            !pending.has(entityKeys.answer(uuid, answer.slot)) &&
            !sameRecord(storedAnswers.get(answer.answerUuid), answer)
        ) {
            /* Op een andere plek gezet (een nieuwe versie van de vragenlijst): de foto's gaan mee. */
            await followAnswer(db, localByUuid.get(answer.answerUuid), answer);
            await db.answers.put(answer);
        }
    }

    /* Aantallen van multi-containers. */
    const serverCounts = inspection.multi_counts.map((count) => multiCountRecordFrom(uuid, count));
    const countSlots = new Set(serverCounts.map((count) => count.slot));
    const storedCounts = new Map(
        (await db.multiCounts.where('inspectionUuid').equals(uuid).toArray()).map((count) => [
            count.slot,
            count,
        ]),
    );

    for (const local of storedCounts.values()) {
        if (!countSlots.has(local.slot) && !pending.has(entityKeys.multiCount(uuid, local.slot))) {
            await db.multiCounts.delete([uuid, local.slot]);
        }
    }

    for (const count of serverCounts) {
        if (
            !pending.has(entityKeys.multiCount(uuid, count.slot)) &&
            !sameRecord(storedCounts.get(count.slot), count)
        ) {
            await db.multiCounts.put(count);
        }
    }

    /* Bevindingen. */
    const findingUuids = new Set(inspection.findings.map((finding) => finding.finding_uuid));
    const storedFindings = new Map(
        (await db.findings.where('inspectionUuid').equals(uuid).toArray()).map((finding) => [
            finding.findingUuid,
            finding,
        ]),
    );

    for (const local of storedFindings.values()) {
        if (
            !findingUuids.has(local.findingUuid) &&
            !pending.has(entityKeys.finding(local.findingUuid))
        ) {
            await db.findings.delete(local.findingUuid);
        }
    }

    for (const finding of inspection.findings) {
        const record = findingRecordFrom(uuid, finding);

        if (
            !pending.has(entityKeys.finding(finding.finding_uuid)) &&
            !sameRecord(storedFindings.get(finding.finding_uuid), record)
        ) {
            await db.findings.put(record);
        }
    }

    /* Foto's: wat de server heeft, plus wat hier nog geüpload moet worden. */
    const answersByUuid = new Map<string, AnswerRecord>(
        (await db.answers.where('inspectionUuid').equals(uuid).toArray()).map((answer) => [
            answer.answerUuid,
            answer,
        ]),
    );
    serverAnswers.forEach((answer) => answersByUuid.set(answer.answerUuid, answer));
    const serverPhotos = new Set(inspection.photos.map((photo) => photo.client_uuid));

    for (const photo of inspection.photos) {
        const answer = photo.answer_uuid ? answersByUuid.get(photo.answer_uuid) : undefined;
        const local = await db.photos.get(photo.client_uuid);
        const record = answer
            ? photoRecordFrom(uuid, photo, answer, local, now)
            : photo.finding_uuid
              ? findingPhotoRecordFrom(
                    uuid,
                    { ...photo, finding_uuid: photo.finding_uuid },
                    local,
                    now,
                )
              : undefined;

        if (record && !sameRecord(local, record)) {
            await db.photos.put(record);
        }
    }

    for (const local of await db.photos.where('inspectionUuid').equals(uuid).toArray()) {
        if (local.state === 'uploaded' && !serverPhotos.has(local.clientUuid)) {
            await db.photos.delete(local.clientUuid);
        }
    }
}

/** Staat er voor deze opname nog iets dat niet op de server is? */
export async function hasLocalWork(db: TenantDatabase, inspectionUuid: string): Promise<boolean> {
    const operations = await db.outbox.where('inspectionUuid').equals(inspectionUuid).count();

    if (operations > 0) {
        return true;
    }

    const photos = await db.photos.where('inspectionUuid').equals(inspectionUuid).toArray();

    return photos.some((photo) => photo.state !== 'uploaded' || photo.deleted === 1);
}

export async function removeInspection(db: TenantDatabase, inspectionUuid: string): Promise<void> {
    await db.answers.where('inspectionUuid').equals(inspectionUuid).delete();
    await db.multiCounts.where('inspectionUuid').equals(inspectionUuid).delete();
    await db.findings.where('inspectionUuid').equals(inspectionUuid).delete();
    await db.photos.where('inspectionUuid').equals(inspectionUuid).delete();
    await db.inspections.delete(inspectionUuid);
}

export interface PackageSummary {
    fetched: string[];
    notModified: string[];
    failed: Array<{ uuid: string; error: unknown }>;
}

const quoted = (checksum: string) => `"${checksum}"`;

function checksumOf(document: unknown): string | null {
    return typeof document === 'object' &&
        document !== null &&
        'checksum' in document &&
        typeof document.checksum === 'string'
        ? document.checksum
        : null;
}

function versionNumberOf(document: unknown): number | null {
    if (typeof document !== 'object' || document === null || !('version' in document)) {
        return null;
    }

    const { version } = document;

    return typeof version === 'object' &&
        version !== null &&
        'version' in version &&
        typeof version.version === 'number'
        ? version.version
        : null;
}

/**
 * Kondigt de vragenlijsten aan waar een opname op dit apparaat naar wijst, maar
 * die hier nog niet bekend zijn.
 *
 * Alleen de bootstrap kondigt versies aan. Een keuring die na de bootstrap werd
 * ingepland, komt via pull binnen met een `template_version_uuid` die hier nog
 * niet staat; zonder deze stap zou het pakket daarvan nooit opgehaald worden en
 * bleef de opname wachten. Het pakket staat op het pad uit het contract;
 * checksum en versienummer komen uit het document zelf.
 */
async function announceReferencedTemplateVersions(db: TenantDatabase): Promise<void> {
    const known = new Set(await db.templateVersions.toCollection().primaryKeys());
    const missing = new Set<string>();

    for (const inspection of await db.inspections.toArray()) {
        if (inspection.templateVersionUuid && !known.has(inspection.templateVersionUuid)) {
            missing.add(inspection.templateVersionUuid);
        }
    }

    for (const uuid of missing) {
        await db.templateVersions.put({
            uuid,
            version: 0,
            checksum: '',
            packageUrl: `/template-versions/${uuid}/package`,
            etag: null,
            byteSize: 0,
            fetchedAt: null,
        });
    }
}

/**
 * Haalt elk pakket op dat nog niet (in deze versie) lokaal staat: van de
 * aangekondigde versies, en van elke versie waar een opname op dit apparaat
 * naar wijst.
 *
 * De ETag van het pakket is de sha-256 van de inhoud - dezelfde waarde als
 * `checksum`. Staat het document er al met die checksum, dan gaat er geen
 * verzoek uit. Anders gaat de bewaarde ETag mee als `If-None-Match`: een `304`
 * houdt het document dat er al staat. Ontbreekt het document zelf, dan komt het
 * volledig opnieuw binnen.
 *
 * Een pakket dat niet binnenkomt breekt de rest niet; de volgende sync probeert
 * het opnieuw.
 */
export async function ensureTemplatePackages(
    db: TenantDatabase,
    api: Pick<SyncApi, 'fetchPackage'>,
    now: () => number = Date.now,
): Promise<PackageSummary> {
    const summary: PackageSummary = { fetched: [], notModified: [], failed: [] };

    await announceReferencedTemplateVersions(db);
    const documents = new Set(await db.packageDocuments.toCollection().primaryKeys());

    for (const version of await db.templateVersions.toArray()) {
        const stored =
            version.fetchedAt !== null && version.etag !== null && documents.has(version.uuid);

        if (stored && version.etag === quoted(version.checksum)) {
            continue;
        }

        try {
            const response = await api.fetchPackage({
                uuid: version.uuid,
                packageUrl: version.packageUrl,
                etag: stored ? version.etag : null,
            });

            if (response.status === 'not-modified') {
                summary.notModified.push(version.uuid);
                await db.templateVersions.update(version.uuid, {
                    etag: quoted(version.checksum),
                    fetchedAt: now(),
                });
                continue;
            }

            const checksum = checksumOf(response.document);

            if (checksum === null) {
                throw new Error(`Pakket ${version.uuid} heeft geen checksum.`);
            }

            await db.transaction('rw', db.templateVersions, db.packageDocuments, async () => {
                await db.packageDocuments.put({ uuid: version.uuid, document: response.document });
                await db.templateVersions.update(version.uuid, {
                    version: versionNumberOf(response.document) ?? version.version,
                    etag: quoted(checksum),
                    checksum,
                    byteSize: response.byteSize,
                    fetchedAt: now(),
                });
            });
            summary.fetched.push(version.uuid);
        } catch (error) {
            summary.failed.push({ uuid: version.uuid, error });
        }
    }

    return summary;
}
